/**
 * Dispatcher — the one path an event takes from a call site to a transport.
 *
 * Every event goes through the same five steps, in this order, and there is
 * no way to skip one:
 *
 *   rate limit -> validate against the catalog -> sanitize -> stamp context
 *   -> fan out to transports
 *
 * Ordering is deliberate. Rate limiting comes first so a render loop costs
 * a map lookup rather than a schema parse. Validation precedes sanitization
 * because the catalog decides which fields may exist and the sanitizer
 * decides whether their values are safe — the reverse would sanitize fields
 * that were about to be discarded anyway.
 *
 * The whole body is wrapped so that no defect in this subsystem can break a
 * page. A missing analytics event is a bad afternoon; a white screen because
 * telemetry threw is a bad quarter.
 *
 * @module logging/pipeline/dispatcher
 */

import type { FlushReason, Transport, WireEvent } from '../core/types';
import type { LoggingConfig } from '../config';
import { loggingConfig } from '../config';
import { diagnostic } from '../core/diagnostic';
import { sanitizeParams, sanitizePath } from '../core/sanitize';
import { getSessionId, nextSequence } from '../core/session';
import { EVENT_CATALOG, freeTextParamsFor } from '../events/catalog';
import type { ParamsFor, WebEventName } from '../events/catalog';
import { createTransports } from '../transports';
import { createRateLimiter } from './rate-limiter';

export interface EmitOptions {
  /**
   * Collapses identical repeats within a short window.
   *
   * Supply it where repetition means malfunction — the same error firing
   * every render. Omit it where repetition is real signal: two views of the
   * same path are two views.
   */
  readonly dedupeKey?: string;
}

export interface Dispatcher {
  emit<N extends WebEventName>(name: N, params: ParamsFor<N>, options?: EmitOptions): void;
  flush(reason: FlushReason): void;
  /** Active transport names. Diagnostics and the dev console banner. */
  readonly describe: () => string[];
}

export function createDispatcher(config: LoggingConfig = loggingConfig): Dispatcher {
  const limiter = createRateLimiter({
    burst: config.rateLimitBurst,
    perMinute: config.rateLimitPerMinute,
  });

  // Built on first use rather than at module load: it keeps the cost off
  // the critical path of a page that never emits, and keeps import order
  // from deciding when Firebase App Check gets touched.
  let transports: Transport[] | null = null;

  function getTransports(): Transport[] {
    transports ??= createTransports(config);
    return transports;
  }

  /** Assemble the wire record. The only place `snake_case` is produced. */
  function build(
    name: WebEventName,
    params: Readonly<Record<string, unknown>>,
  ): WireEvent {
    return {
      event_name: name,
      surface: 'web',
      session_id: getSessionId(),
      path: sanitizePath(window.location.pathname),
      ts_client: new Date().toISOString(),
      app_version: config.appVersion,
      severity: EVENT_CATALOG[name].severity,
      event_seq: nextSequence(),
      ...sanitizeParams(params, freeTextParamsFor(name)),
    };
  }

  return {
    emit<N extends WebEventName>(name: N, params: ParamsFor<N>, options?: EmitOptions): void {
      if (!config.enabled) return;

      try {
        if (!limiter.allow(name, options?.dedupeKey)) return;

        // `safeParse`, never `parse`: a call site that drifts from the
        // catalog should lose its event, not throw into a click handler.
        // Unknown keys are stripped here, which is also what stops anyone
        // writing arbitrary columns into the BigQuery sink.
        const parsed = EVENT_CATALOG[name].params.safeParse(params);
        if (!parsed.success) {
          diagnostic(`invalid params for ${name}`, parsed.error.issues);
          return;
        }

        const event = build(name, parsed.data as Record<string, unknown>);
        for (const transport of getTransports()) {
          transport.accept(event);
        }
      } catch (error) {
        diagnostic(`emit failed for ${name}`, error);
      }
    },

    flush(reason: FlushReason): void {
      if (!config.enabled) return;
      try {
        for (const transport of getTransports()) {
          transport.flush(reason);
        }
      } catch (error) {
        diagnostic('flush failed', error);
      }
    },

    describe: () => getTransports().map((t) => t.name),
  };
}
